import { GUI } from "dat.gui";
import { GUserInputDesc } from "./core/input";
import { CreateFramebufferWithAttachment, CreateTextureRT, FrameBufferCheck } from "./core/resourcesUtils";
import { Shader } from "./core/shaderUtils";
import { GetVec2, Vector2, Vector3 } from "./core/types";
import { GTime } from "./core/utils";
import { GLSetTexture } from "./helpers/glHelper";
import { CommonRenderingResources } from "./shaders/shaderConfig";
import { ShaderSourceFullscreenPassVS } from "./shaders/shaderPostProcess";
import {
    GetShaderSourceAdvectDensityMacKormackPS,
    GetShaderSourceAdvectDensityPS,
    GetShaderSourceAdvectVelocityPS,
    GetShaderSourceApplyVelocityPS,
    GetShaderSourceApplyVelocityVS,
    GetShaderSourceApplyVorticityPS,
    GetShaderSourceClearPressurePS,
    GetShaderSourceComputeCurlPS,
    GetShaderSourceComputeDivergencePS,
    GetShaderSourceComputePressurePS,
    GetShaderSourceDiffuseVelocityPS,
    GetShaderSourceInjectDensityColorVS,
    GetShaderSourceInjectDensityPS,
    GetShaderSourceInjectDensitySceneColorPS,
    GetShaderSourceInjectDensityTexturedPS,
    GetShaderSourceInjectImpulsePS,
    GetShaderSourceProjectPressurePS,
    GetShaderSourceResolveDivergencePS,
} from "./shaders/shaderVectorField";
import { GRenderTargets } from "./core/renderTargets";

function GetUniformParametersList(gl: WebGL2RenderingContext, shaderProgram: WebGLProgram) {
    const params = {
        ScreenRatio: gl.getUniformLocation(shaderProgram, "ScreenRatio"),
        Position: gl.getUniformLocation(shaderProgram, "Position"),
        Radius: gl.getUniformLocation(shaderProgram, "Radius"),
        Velocity: gl.getUniformLocation(shaderProgram, "Velocity"),
        VecFieldXTexture: gl.getUniformLocation(shaderProgram, "VecFieldXTexture"),
        VecFieldYTexture: gl.getUniformLocation(shaderProgram, "VecFieldYTexture"),
        DivergenceTexture: gl.getUniformLocation(shaderProgram, "DivergenceTexture"),
        PressureTexture: gl.getUniformLocation(shaderProgram, "PressureTexture"),
        DeltaTime: gl.getUniformLocation(shaderProgram, "DeltaTime"),
        Time: gl.getUniformLocation(shaderProgram, "Time"),
        PressureClearValue: gl.getUniformLocation(shaderProgram, "PressureClearValue"),
        CurlTexture: gl.getUniformLocation(shaderProgram, "CurlTexture"),
        VorticityScale: gl.getUniformLocation(shaderProgram, "VorticityScale"),
        Decay: gl.getUniformLocation(shaderProgram, "Decay"),
        DensityTexture: gl.getUniformLocation(shaderProgram, "DensityTexture"),
        DensityAdvectedTexture: gl.getUniformLocation(shaderProgram, "DensityAdvectedTexture"),
        DensityReversedTexture: gl.getUniformLocation(shaderProgram, "DensityReversedTexture"),
        ImpulseStrength: gl.getUniformLocation(shaderProgram, "ImpulseStrength"),
        ViewportRatio: gl.getUniformLocation(shaderProgram, "ViewportRatio"),
        ViewportSize: gl.getUniformLocation(shaderProgram, "ViewportSize"),
        PositionNDC: gl.getUniformLocation(shaderProgram, "PositionNDC"),
        Scale: gl.getUniformLocation(shaderProgram, "Scale"),
        ColorTexture: gl.getUniformLocation(shaderProgram, "ColorTexture"),
        ColorFilter: gl.getUniformLocation(shaderProgram, "ColorFilter"),
        EProcNoiseMode: gl.getUniformLocation(shaderProgram, "EProcNoiseMode"),
    };
    return params;
}

export class VectorField {
    static GFullscreenInstance: VectorField | null = null;

    readonly bAdvectDensityMacKormack = false;

    NumCells = GetVec2(0, 0);
    DensityNumCells = GetVec2(0, 0);
    DensityFieldXYRatio;
    bAlphaChannelEnabled;

    TextureX;
    TextureY;
    FrameBuffer: WebGLFramebuffer[];
    CurVelTextureIndex = 0;
    CurDensTextureIndex = 0;
    CurPressureTextureIndex = 0;

    DivergenceTexture;
    DivergenceFrameBuffer;

    PressureTexture;
    PressureFramebuffer;

    CurlTexture;
    CurlFramebuffer;

    DensityTexture;
    DensityFramebuffer;
    DensityMaskTexture;
    DensityAlphaTexture;
    DensityAlphaFramebuffer;
    DensityAlphaMRTFramebuffer;
    //For McKrmck Advection
    DensityTextureTemp;
    DensityFramebufferTemp;

    ShaderComputeDivergence: Shader;
    ShaderComputePressure: Shader;
    ShaderClearPressure: Shader;
    ShaderProjectPressure: Shader;
    ShaderComputeCurl: Shader;
    ShaderApplyVorticity: Shader;
    ShaderAdvectVelocity: Shader;
    ShaderDiffuseVelocity: Shader;
    ShaderAdvectDensity: Shader;
    ShaderAdvectDensityAlpha: Shader;
    ShaderAdvectDensityReversed: Shader;
    ShaderAdvectDensityMacKormackFinal: Shader;
    ShaderResolveDivergence: Shader;

    //Settings:
    PressureClearValue = 0.95;
    ProjectionNumIterations = 2;
    VorticityScale = 2.0;
    VelocityDecay = 0.01;
    VelocityDiffusion = 0.5;
    bDiffuseVelocity = true;
    DensityDecay = 0.0;
    DensityAlphaDecay = 0.0;
    bDensityHDR = true;
    EProcNoiseMode = 0;

    SubmitDebugUI(datGui: dat.GUI): GUI {
        const folder = datGui.addFolder("VectorField");

        //folder.open();

        folder.add(this, "PressureClearValue", 0, 1).name("PressureClearValue").step(0.01);
        folder.add(this, "ProjectionNumIterations", 0, 20).name("ProjNumIterations").step(1);
        folder.add(this, "VorticityScale", 0, 50).name("Curl Scale").step(0.01);
        folder.add(this, "VelocityDecay", 0, 5).name("Velocity Decay").step(0.01);

        folder.add(this, "bDiffuseVelocity");
        folder.add(this, "VelocityDiffusion", 0, 2).name("Velocity Diffusion").step(0.01);

        folder.add(this, "DensityDecay", 0, 5).name("Density Decay").step(0.01);
        folder.add(this, "DensityAlphaDecay", 0, 5).name("Density Alpha Decay").step(0.01);

        return folder;
    }

    constructor(gl: WebGL2RenderingContext, inSize: Vector2, inDensitySize: Vector2, bEnableAlpha: boolean) {
        this.bAlphaChannelEnabled = bEnableAlpha;
        this.NumCells.x = inSize.x;
        this.NumCells.y = inSize.y;
        this.DensityNumCells.x = inDensitySize.x;
        this.DensityNumCells.y = inDensitySize.y;
        this.DensityFieldXYRatio = inDensitySize.x / inDensitySize.y;

        //Texture
        this.TextureX = [];
        this.TextureX[0] = CreateTextureRT(gl, this.NumCells, gl.R16F, gl.RED, gl.HALF_FLOAT);
        this.TextureX[1] = CreateTextureRT(gl, this.NumCells, gl.R16F, gl.RED, gl.HALF_FLOAT);
        this.TextureY = [];
        this.TextureY[0] = CreateTextureRT(gl, this.NumCells, gl.R16F, gl.RED, gl.HALF_FLOAT);
        this.TextureY[1] = CreateTextureRT(gl, this.NumCells, gl.R16F, gl.RED, gl.HALF_FLOAT);

        //FBO
        this.FrameBuffer = [];
        this.FrameBuffer[0] = gl.createFramebuffer()!;
        this.FrameBuffer[1] = gl.createFramebuffer()!;

        //link our RTs to Framebuffers
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FrameBuffer[0]);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.TextureX[0], 0);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, this.TextureY[0], 0);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
        FrameBufferCheck(gl, "VecFieldFrameBuffer");

        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FrameBuffer[1]);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.TextureX[1], 0);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, this.TextureY[1], 0);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
        FrameBufferCheck(gl, "VecFieldFrameBuffer");

        this.DivergenceTexture = CreateTextureRT(gl, this.NumCells, gl.R16F, gl.RED, gl.HALF_FLOAT);
        this.DivergenceFrameBuffer = CreateFramebufferWithAttachment(gl, this.DivergenceTexture!)!;

        this.CurlTexture = CreateTextureRT(gl, this.NumCells, gl.R16F, gl.RED, gl.HALF_FLOAT);
        this.CurlFramebuffer = CreateFramebufferWithAttachment(gl, this.CurlTexture!)!;

        let textureInternalFormat: number = gl.R11F_G11F_B10F;
        let textureFormat: number = gl.RGB;
        let textureType: number = gl.HALF_FLOAT;
        if (!this.bDensityHDR) {
            textureInternalFormat = gl.RGBA8;
            textureFormat = gl.RGBA;
            textureType = gl.UNSIGNED_BYTE;
        }
        {
            this.DensityTexture = [];
            this.DensityFramebuffer = [];
            this.DensityTexture[0] = CreateTextureRT(
                gl,
                this.DensityNumCells,
                textureInternalFormat,
                textureFormat,
                textureType,
            );
            this.DensityFramebuffer[0] = CreateFramebufferWithAttachment(gl, this.DensityTexture[0]!)!;
            this.DensityTexture[1] = CreateTextureRT(
                gl,
                this.DensityNumCells,
                textureInternalFormat,
                textureFormat,
                textureType,
            );
            this.DensityFramebuffer[1] = CreateFramebufferWithAttachment(gl, this.DensityTexture[1]!)!;
        }
        if (this.bAdvectDensityMacKormack) {
            this.DensityTextureTemp = [];
            this.DensityFramebufferTemp = [];
            this.DensityTextureTemp[0] = CreateTextureRT(
                gl,
                this.DensityNumCells,
                textureInternalFormat,
                textureFormat,
                textureType,
            );
            this.DensityFramebufferTemp[0] = CreateFramebufferWithAttachment(gl, this.DensityTextureTemp[0]!)!;
            this.DensityTextureTemp[1] = CreateTextureRT(
                gl,
                this.DensityNumCells,
                textureInternalFormat,
                textureFormat,
                textureType,
            );
            this.DensityFramebufferTemp[1] = CreateFramebufferWithAttachment(gl, this.DensityTextureTemp[1]!)!;
        }

        this.DensityMaskTexture = CreateTextureRT(gl, this.DensityNumCells, gl.R8, gl.RED, gl.UNSIGNED_BYTE);

        if (bEnableAlpha) {
            textureInternalFormat = gl.R16F;
            textureFormat = gl.RED;
            textureType = gl.HALF_FLOAT;
            this.DensityAlphaTexture = [];
            this.DensityAlphaFramebuffer = [];
            this.DensityAlphaTexture[0] = CreateTextureRT(
                gl,
                this.DensityNumCells,
                textureInternalFormat,
                textureFormat,
                textureType,
            );
            this.DensityAlphaFramebuffer[0] = CreateFramebufferWithAttachment(gl, this.DensityAlphaTexture[0]!)!;
            this.DensityAlphaTexture[1] = CreateTextureRT(
                gl,
                this.DensityNumCells,
                textureInternalFormat,
                textureFormat,
                textureType,
            );
            this.DensityAlphaFramebuffer[1] = CreateFramebufferWithAttachment(gl, this.DensityAlphaTexture[1]!)!;

            this.DensityAlphaMRTFramebuffer = [];
            const createMRTFBO = (i: number) => {
                this.DensityAlphaMRTFramebuffer![i] = gl.createFramebuffer()!;
                gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityAlphaMRTFramebuffer![i]!);
                gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.DensityTexture[i], 0);
                gl.framebufferTexture2D(
                    gl.FRAMEBUFFER,
                    gl.COLOR_ATTACHMENT1,
                    gl.TEXTURE_2D,
                    this.DensityAlphaTexture![i]!,
                    0,
                );
                gl.framebufferTexture2D(
                    gl.FRAMEBUFFER,
                    gl.COLOR_ATTACHMENT2,
                    gl.TEXTURE_2D,
                    this.DensityMaskTexture,
                    0,
                );
            };

            createMRTFBO(0);
            createMRTFBO(1);
        }

        this.PressureTexture = [];
        this.PressureTexture[0] = CreateTextureRT(gl, this.NumCells, gl.R16F, gl.RED, gl.HALF_FLOAT);
        this.PressureTexture[1] = CreateTextureRT(gl, this.NumCells, gl.R16F, gl.RED, gl.HALF_FLOAT);

        this.PressureFramebuffer = [];
        this.PressureFramebuffer[0] = CreateFramebufferWithAttachment(gl, this.PressureTexture[0]!)!;
        this.PressureFramebuffer[1] = CreateFramebufferWithAttachment(gl, this.PressureTexture[1]!)!;

        //Create Shader Program

        this.ShaderComputeDivergence = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceComputeDivergencePS(this.NumCells),
            GetUniformParametersList,
        );
        this.ShaderComputeCurl = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceComputeCurlPS(this.NumCells),
            GetUniformParametersList,
        );
        this.ShaderApplyVorticity = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceApplyVorticityPS(this.NumCells),
            GetUniformParametersList,
        );
        this.ShaderComputePressure = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceComputePressurePS(this.NumCells),
            GetUniformParametersList,
        );
        this.ShaderClearPressure = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceClearPressurePS(this.NumCells),
            GetUniformParametersList,
        );
        this.ShaderProjectPressure = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceProjectPressurePS(this.NumCells),
            GetUniformParametersList,
        );
        this.ShaderAdvectVelocity = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceAdvectVelocityPS(this.NumCells),
            GetUniformParametersList,
        );
        this.ShaderDiffuseVelocity = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceDiffuseVelocityPS(this.NumCells),
            GetUniformParametersList,
        );
        this.ShaderAdvectDensity = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceAdvectDensityPS(this.DensityNumCells, false, false),
            GetUniformParametersList,
        );
        this.ShaderAdvectDensityAlpha = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceAdvectDensityPS(this.DensityNumCells, false, true),
            GetUniformParametersList,
        );
        this.ShaderAdvectDensityReversed = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceAdvectDensityPS(this.DensityNumCells, true, false),
            GetUniformParametersList,
        );
        this.ShaderAdvectDensityMacKormackFinal = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceAdvectDensityMacKormackPS(this.DensityNumCells),
            GetUniformParametersList,
        );
        this.ShaderResolveDivergence = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceResolveDivergencePS(this.NumCells),
            GetUniformParametersList,
        );
    }

    UpdateMain(gl: WebGL2RenderingContext) {
        if (this.bDiffuseVelocity) {
            this.DiffuseVelocity(gl);
        }

        this.ApplyVorticity(gl);

        this.ProjectVelocity(gl);

        if (this.bAdvectDensityMacKormack) {
            this.AdvectDensityMcKrmck(gl);
        } else {
            this.AdvectDensity(gl);
        }
    }

    private ComputeDivergence(gl: WebGL2RenderingContext) {
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.DivergenceFrameBuffer);

        gl.useProgram(this.ShaderComputeDivergence.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(
            gl,
            this.ShaderComputeDivergence.UniformParametersLocationList.VecFieldXTexture,
            this.GetCurTextureX(),
            0,
        );
        GLSetTexture(
            gl,
            this.ShaderComputeDivergence.UniformParametersLocationList.VecFieldYTexture,
            this.GetCurTextureY(),
            1,
        );

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    private ComputeCurl(gl: WebGL2RenderingContext) {
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.CurlFramebuffer);

        gl.useProgram(this.ShaderComputeCurl.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(
            gl,
            this.ShaderComputeCurl.UniformParametersLocationList.VecFieldXTexture,
            this.GetCurTextureX(),
            0,
        );
        GLSetTexture(
            gl,
            this.ShaderComputeCurl.UniformParametersLocationList.VecFieldYTexture,
            this.GetCurTextureY(),
            1,
        );

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    private ApplyVorticity(gl: WebGL2RenderingContext) {
        this.ComputeCurl(gl);

        const curShader = this.ShaderApplyVorticity;
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FrameBuffer[1 - this.CurVelTextureIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(gl, curShader.UniformParametersLocationList.CurlTexture, this.CurlTexture, 0);
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldXTexture,
            this.TextureX[this.CurVelTextureIndex],
            1,
        );
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldYTexture,
            this.TextureY[this.CurVelTextureIndex],
            2,
        );

        gl.uniform1f(curShader.UniformParametersLocationList.DeltaTime, GTime.Delta * GTime.DeltaTimeScale);
        gl.uniform1f(curShader.UniformParametersLocationList.VorticityScale, this.VorticityScale);

        gl.drawArrays(gl.TRIANGLES, 0, 3);

        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

        this.CurVelTextureIndex = 1 - this.CurVelTextureIndex;
    }

    private ComputePressure(gl: WebGL2RenderingContext, destIndex: number) {
        const curShader = this.ShaderComputePressure;
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.PressureFramebuffer[destIndex]);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(gl, curShader.UniformParametersLocationList.DivergenceTexture, this.DivergenceTexture, 0);

        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.PressureTexture,
            this.PressureTexture[1 - destIndex],
            1,
        );

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    private ClearPressure(gl: WebGL2RenderingContext, destIndex: number) {
        const curShader = this.ShaderClearPressure;
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.PressureFramebuffer[destIndex]);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.PressureTexture,
            this.PressureTexture[1 - destIndex],
            1,
        );

        gl.uniform1f(curShader.UniformParametersLocationList.PressureClearValue, this.PressureClearValue);

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    private ProjectPressure(gl: WebGL2RenderingContext, pressureSrcIndex: number) {
        const curShader = this.ShaderProjectPressure;
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FrameBuffer[1 - this.CurVelTextureIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.PressureTexture,
            this.PressureTexture[pressureSrcIndex],
            0,
        );
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldXTexture,
            this.TextureX[this.CurVelTextureIndex],
            1,
        );
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldYTexture,
            this.TextureY[this.CurVelTextureIndex],
            2,
        );

        gl.drawArrays(gl.TRIANGLES, 0, 3);

        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

        this.CurVelTextureIndex = 1 - this.CurVelTextureIndex;
    }

    private ResolveDivergence(gl: WebGL2RenderingContext) {
        const curShader = this.ShaderResolveDivergence;
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FrameBuffer[1 - this.CurVelTextureIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(gl, curShader.UniformParametersLocationList.DivergenceTexture, this.DivergenceTexture, 0);
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldXTexture,
            this.TextureX[this.CurVelTextureIndex],
            1,
        );
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldYTexture,
            this.TextureY[this.CurVelTextureIndex],
            2,
        );

        gl.drawArrays(gl.TRIANGLES, 0, 3);

        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

        this.CurVelTextureIndex = 1 - this.CurVelTextureIndex;
    }

    DiffuseVelocity(gl: WebGL2RenderingContext) {
        const curShader = this.ShaderDiffuseVelocity;
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FrameBuffer[1 - this.CurVelTextureIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldXTexture,
            this.TextureX[this.CurVelTextureIndex],
            1,
        );
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldYTexture,
            this.TextureY[this.CurVelTextureIndex],
            2,
        );

        gl.uniform1f(curShader.UniformParametersLocationList.DeltaTime, GTime.Delta * GTime.DeltaTimeScale);
        gl.uniform1f(curShader.UniformParametersLocationList.Decay, this.VelocityDiffusion);

        gl.drawArrays(gl.TRIANGLES, 0, 3);

        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

        this.CurVelTextureIndex = 1 - this.CurVelTextureIndex;
    }

    private AdvectVelocity(gl: WebGL2RenderingContext) {
        const curShader = this.ShaderAdvectVelocity;
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FrameBuffer[1 - this.CurVelTextureIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldXTexture,
            this.TextureX[this.CurVelTextureIndex],
            1,
        );
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldYTexture,
            this.TextureY[this.CurVelTextureIndex],
            2,
        );

        gl.uniform1f(curShader.UniformParametersLocationList.DeltaTime, GTime.Delta * GTime.DeltaTimeScale);
        gl.uniform1f(curShader.UniformParametersLocationList.Decay, this.VelocityDecay);
        gl.uniform1f(curShader.UniformParametersLocationList.Time, GTime.Cur);

        gl.drawArrays(gl.TRIANGLES, 0, 3);

        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

        this.CurVelTextureIndex = 1 - this.CurVelTextureIndex;
    }
    AdvectDensity(gl: WebGL2RenderingContext) {
        //gl.disable(gl.BLEND);
        let curShader = this.ShaderAdvectDensity;
        gl.viewport(0, 0, this.DensityNumCells.x, this.DensityNumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityFramebuffer[1 - this.CurDensTextureIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldXTexture,
            this.TextureX[this.CurVelTextureIndex],
            1,
        );
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.VecFieldYTexture,
            this.TextureY[this.CurVelTextureIndex],
            2,
        );
        GLSetTexture(
            gl,
            curShader.UniformParametersLocationList.DensityTexture,
            this.DensityTexture[this.CurDensTextureIndex],
            3,
        );

        gl.uniform1f(curShader.UniformParametersLocationList.DeltaTime, GTime.Delta * GTime.DeltaTimeScale);
        gl.uniform1f(curShader.UniformParametersLocationList.Time, GTime.Cur);
        gl.uniform1f(curShader.UniformParametersLocationList.Decay, this.DensityDecay);

        gl.uniform1i(curShader.UniformParametersLocationList.EProcNoiseMode, this.EProcNoiseMode);

        gl.drawArrays(gl.TRIANGLES, 0, 3);

        if (this.bAlphaChannelEnabled) {
            curShader = this.ShaderAdvectDensityAlpha;
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityAlphaFramebuffer![1 - this.CurDensTextureIndex]);
            gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

            gl.useProgram(curShader.ShaderProgram);
            gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.VecFieldXTexture,
                this.TextureX[this.CurVelTextureIndex],
                1,
            );
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.VecFieldYTexture,
                this.TextureY[this.CurVelTextureIndex],
                2,
            );
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.DensityTexture,
                this.DensityAlphaTexture![this.CurDensTextureIndex],
                3,
            );

            gl.uniform1f(curShader.UniformParametersLocationList.DeltaTime, GTime.Delta * GTime.DeltaTimeScale);
            gl.uniform1f(curShader.UniformParametersLocationList.Time, GTime.Cur);
            gl.uniform1f(curShader.UniformParametersLocationList.Decay, this.DensityAlphaDecay);

            gl.uniform1i(curShader.UniformParametersLocationList.EProcNoiseMode, this.EProcNoiseMode);

            gl.drawArrays(gl.TRIANGLES, 0, 3);
        }

        this.CurDensTextureIndex = 1 - this.CurDensTextureIndex;
    }

    AdvectDensityMcKrmck(gl: WebGL2RenderingContext) {
        if (!this.bAdvectDensityMacKormack) {
            return;
        }
        //1. advect as usual but into separate buffer
        {
            const curShader = this.ShaderAdvectDensity;
            gl.viewport(0, 0, this.DensityNumCells.x, this.DensityNumCells.y);
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityFramebufferTemp![0]);
            gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

            gl.useProgram(curShader.ShaderProgram);
            gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.VecFieldXTexture,
                this.TextureX[this.CurVelTextureIndex],
                1,
            );
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.VecFieldYTexture,
                this.TextureY[this.CurVelTextureIndex],
                2,
            );
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.DensityTexture,
                this.DensityTexture[this.CurDensTextureIndex],
                3,
            );

            gl.uniform1f(curShader.UniformParametersLocationList.DeltaTime, GTime.Delta * GTime.DeltaTimeScale);
            gl.uniform1f(curShader.UniformParametersLocationList.Decay, 0.0);

            gl.drawArrays(gl.TRIANGLES, 0, 3);
        }
        //2. reverse advect forward into another separate buffer using prev buffer as SRV
        {
            const curShader = this.ShaderAdvectDensityReversed;
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityFramebufferTemp![1]);

            gl.useProgram(curShader.ShaderProgram);
            gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.VecFieldXTexture,
                this.TextureX[this.CurVelTextureIndex],
                1,
            );
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.VecFieldYTexture,
                this.TextureY[this.CurVelTextureIndex],
                2,
            );
            GLSetTexture(gl, curShader.UniformParametersLocationList.DensityTexture, this.DensityTextureTemp![0], 3);

            gl.uniform1f(curShader.UniformParametersLocationList.DeltaTime, GTime.Delta * GTime.DeltaTimeScale);
            gl.uniform1f(curShader.UniformParametersLocationList.Decay, 0.0);

            gl.drawArrays(gl.TRIANGLES, 0, 3);
        }
        //3. combine results and clamp
        {
            const curShader = this.ShaderAdvectDensityMacKormackFinal;
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityFramebuffer[1 - this.CurDensTextureIndex]);

            gl.useProgram(curShader.ShaderProgram);
            gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.VecFieldXTexture,
                this.TextureX[this.CurVelTextureIndex],
                1,
            );
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.VecFieldYTexture,
                this.TextureY[this.CurVelTextureIndex],
                2,
            );
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.DensityTexture,
                this.DensityTexture[this.CurDensTextureIndex],
                3,
            );
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.DensityAdvectedTexture,
                this.DensityTextureTemp![0],
                4,
            );
            GLSetTexture(
                gl,
                curShader.UniformParametersLocationList.DensityReversedTexture,
                this.DensityTextureTemp![1],
                5,
            );

            gl.uniform1f(curShader.UniformParametersLocationList.DeltaTime, GTime.Delta * GTime.DeltaTimeScale);
            gl.uniform1f(curShader.UniformParametersLocationList.Decay, this.DensityDecay);

            gl.drawArrays(gl.TRIANGLES, 0, 3);
        }

        this.CurDensTextureIndex = 1 - this.CurDensTextureIndex;
    }

    ProjectVelocity(gl: WebGL2RenderingContext) {
        this.ComputeDivergence(gl);

        this.ClearPressure(gl, 1 - this.CurPressureTextureIndex);

        for (let i = 0; i < this.ProjectionNumIterations; i++) {
            this.ComputePressure(gl, this.CurPressureTextureIndex);
            this.CurPressureTextureIndex = 1 - this.CurPressureTextureIndex;
        }

        this.ProjectPressure(gl, 1 - this.CurPressureTextureIndex);

        //this.ResolveDivergence(gl);

        this.AdvectVelocity(gl);
    }

    ClearVectorField(gl: WebGL2RenderingContext) {
        this.BindCurVelocityRT(gl);
        gl.clearColor(0.0, 0.0, 0.0, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT);
    }
    ClearDensity(gl: WebGL2RenderingContext) {
        gl.viewport(0, 0, this.DensityNumCells.x, this.DensityNumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityFramebuffer[this.CurDensTextureIndex]);
        gl.clearColor(0.0, 0.0, 0.0, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT);
    }

    BindCurVelocityRT(gl: WebGL2RenderingContext) {
        const curSourceIndex = this.CurVelTextureIndex;
        gl.viewport(0, 0, this.NumCells.x, this.NumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.FrameBuffer[curSourceIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
    }
    BindCurDensityRT(gl: WebGL2RenderingContext) {
        const curSourceIndex = this.CurDensTextureIndex;
        gl.viewport(0, 0, this.DensityNumCells.x, this.DensityNumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityFramebuffer[curSourceIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
    }
    BindCurDensityAlphaRT(gl: WebGL2RenderingContext) {
        const curSourceIndex = this.CurDensTextureIndex;
        gl.viewport(0, 0, this.DensityNumCells.x, this.DensityNumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityAlphaFramebuffer![curSourceIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
    }
    BindCurDensityAlphaMRTRT(gl: WebGL2RenderingContext) {
        const curSourceIndex = this.CurDensTextureIndex;
        gl.viewport(0, 0, this.DensityNumCells.x, this.DensityNumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.DensityAlphaMRTFramebuffer![curSourceIndex]!);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2]);
    }

    GetCurTextureX() {
        return this.TextureX[this.CurVelTextureIndex];
    }
    GetCurTextureDensity() {
        return this.DensityTexture[this.CurDensTextureIndex];
    }
    GetCurTextureDensityAlpha() {
        return this.DensityAlphaTexture![this.CurDensTextureIndex];
    }
    GetCurTexturePressure() {
        return this.PressureTexture[this.CurPressureTextureIndex];
    }
    GetCurTextureY() {
        return this.TextureY[this.CurVelTextureIndex];
    }
}

export class GVectorFieldInjector {
    static GInstance: GVectorFieldInjector | null = null;

    ShaderInjectVelocity: Shader;
    ShaderInjectDensity: Shader;
    ShaderInjectImpulse: Shader;
    ShaderInjectDenistyTexture: Shader;
    ShaderInjectDenistySceneColor: Shader;

    AppliedVelocityRadius = 0.35;
    AppliedVelocityStrength = 1.0;
    ImpulseRadius = 0.25;
    ImpulseStrength = 2.0;

    SubmitDebugUI(datGui: dat.GUI): GUI {
        const folder = datGui.addFolder("VelFieldInjector");

        //folder.open();

        folder.add(this, "AppliedVelocityRadius", 0, 5).name("Radius").step(0.01);
        folder.add(this, "AppliedVelocityStrength", 0, 5).name("Strength").step(0.1);
        folder.add(this, "ImpulseRadius", 0, 2).name("Impulse Radius").step(0.01);
        folder.add(this, "ImpulseStrength", 0, 5).name("Impulse Strength").step(0.01);

        return folder;
    }

    constructor(gl: WebGL2RenderingContext) {
        this.ShaderInjectVelocity = new Shader(
            gl,
            GetShaderSourceApplyVelocityVS(),
            GetShaderSourceApplyVelocityPS(),
            GetUniformParametersList,
        );
        this.ShaderInjectImpulse = new Shader(
            gl,
            GetShaderSourceApplyVelocityVS(),
            GetShaderSourceInjectImpulsePS(),
            GetUniformParametersList,
        );
        this.ShaderInjectDensity = new Shader(
            gl,
            GetShaderSourceApplyVelocityVS(),
            GetShaderSourceInjectDensityPS(),
            GetUniformParametersList,
        );
        this.ShaderInjectDenistyTexture = new Shader(
            gl,
            GetShaderSourceInjectDensityColorVS(),
            GetShaderSourceInjectDensityTexturedPS(),
            GetUniformParametersList,
        );
        this.ShaderInjectDenistySceneColor = new Shader(
            gl,
            GetShaderSourceInjectDensityColorVS(),
            GetShaderSourceInjectDensitySceneColorPS(),
            GetUniformParametersList,
        );
    }

    /* InjectVelocityFromUserInput(gl: WebGL2RenderingContext, vecField: VectorField) {
        //map input to world
        const posWS = GStack.TempVec3;
        TransformFromNDCToWorld(GUserInputDesc.InputPosCurNDC, -GSceneDesc.Camera.Position.z, posWS);
        //map world to density plane
        const sceneExtent = SceneBase.UniformBuffer.Params.SceneBoundsExtent;
        posWS.x /= sceneExtent.x;
        posWS.y /= sceneExtent.y;
        const vel = GetVec2(0, 0);
        vel.x = GUserInputDesc.InputVelocityCurViewSpace.x * GTime.Delta * 100.0;
        vel.y = GUserInputDesc.InputVelocityCurViewSpace.y * GTime.Delta * 100.0;

        this.InjectVelocity(gl, vecField, posWS, vel);
    } */

    InjectVelocityFromUserInput(gl: WebGL2RenderingContext, vecField: VectorField, radius: number, strength: number) {
        //map input to world
        const vel = GetVec2(0, 0);
        vel.x = GUserInputDesc.InputVelocityCurViewSpace.x * GTime.Delta * 100.0;
        vel.y = GUserInputDesc.InputVelocityCurViewSpace.y * GTime.Delta * 100.0;

        this.InjectVelocity(gl, vecField, GUserInputDesc.InputPosCurNDC, vel, radius, strength);
    }

    InjectVelocity(
        gl: WebGL2RenderingContext,
        vecField: VectorField,
        posTextureSpace: Vector2,
        inVel: Vector2,
        radius: number,
        strength: number,
    ) {
        const curShader = this.ShaderInjectVelocity;
        vecField.BindCurVelocityRT(gl);

        gl.enable(gl.BLEND);
        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.ONE, gl.ONE);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.PlaneShapeVAO);

        const ParametersLocationListRef = curShader.UniformParametersLocationList;
        gl.uniform1f(ParametersLocationListRef.ScreenRatio, vecField.DensityFieldXYRatio);

        gl.uniform2f(ParametersLocationListRef.Position, posTextureSpace.x, posTextureSpace.y);
        gl.uniform1f(ParametersLocationListRef.Radius, radius * 0.1);
        gl.uniform2f(ParametersLocationListRef.Velocity, inVel.x * strength * 10.0, inVel.y * strength * 10.0);

        gl.drawArrays(gl.TRIANGLES, 0, 6);

        gl.disable(gl.BLEND);

        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
    }

    InjectDensity(gl: WebGL2RenderingContext, vecField: VectorField, posTextureSpace: Vector2, inColor: Vector3) {
        vecField.BindCurDensityRT(gl);

        /* gl.enable(gl.BLEND);
        gl.blendEquation(gl.MAX);
        gl.blendFunc(gl.ONE, gl.ONE); */

        {
            const curShader = this.ShaderInjectDensity;
            gl.useProgram(curShader.ShaderProgram);
            gl.bindVertexArray(CommonRenderingResources.PlaneShapeVAO);

            const ParametersLocationListRef = curShader.UniformParametersLocationList;
            gl.uniform1f(ParametersLocationListRef.ScreenRatio, vecField.DensityFieldXYRatio);

            //map input to world
            /* const posWS = GStack.TempVec3;
            TransformFromNDCToWorld(GUserInputDesc.InputPosCurNDC, -GSceneDesc.Camera.Position.z, posWS); */
            //map world to density plane
            /* const sceneExtent = SceneBase.UniformBuffer.Params.SceneBoundsExtent;
            posWS.x /= sceneExtent.x;
            posWS.y /= sceneExtent.y; */

            gl.uniform2f(ParametersLocationListRef.Position, posTextureSpace.x, posTextureSpace.y);
            gl.uniform1f(ParametersLocationListRef.Radius, this.AppliedVelocityRadius * 0.1);

            gl.uniform3f(ParametersLocationListRef.ColorFilter, inColor.x, inColor.y, inColor.z);

            gl.drawArrays(gl.TRIANGLES, 0, 6);
        }

        //gl.disable(gl.BLEND);
    }

    InjectImpulse(gl: WebGL2RenderingContext, vf: VectorField, pos: Vector2) {
        const curShader = this.ShaderInjectImpulse;
        vf.BindCurVelocityRT(gl);

        gl.enable(gl.BLEND);
        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.ONE, gl.ONE);

        gl.useProgram(curShader.ShaderProgram);
        gl.bindVertexArray(CommonRenderingResources.PlaneShapeVAO);

        const ParametersLocationListRef = curShader.UniformParametersLocationList;
        gl.uniform1f(ParametersLocationListRef.ScreenRatio, vf.DensityFieldXYRatio);

        gl.uniform2f(ParametersLocationListRef.Position, pos.x, pos.y);
        gl.uniform1f(ParametersLocationListRef.Radius, this.ImpulseRadius);
        gl.uniform1f(ParametersLocationListRef.ImpulseStrength, this.ImpulseStrength * 10);

        gl.drawArrays(gl.TRIANGLES, 0, 6);

        gl.disable(gl.BLEND);

        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
    }

    InjectDensityTextured(
        gl: WebGL2RenderingContext,
        vecField: VectorField,
        posNDC: Vector2,
        scale: Vector2,
        pTexture: WebGLTexture,
        colorFilter: Vector3 | null = null,
    ) {
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        gl.blendEquation(gl.MAX);
        const curSourceIndex = vecField.CurDensTextureIndex;
        gl.viewport(0, 0, vecField.DensityNumCells.x, vecField.DensityNumCells.y);
        gl.bindFramebuffer(gl.FRAMEBUFFER, vecField.DensityFramebuffer[curSourceIndex]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

        const curShader = this.ShaderInjectDenistyTexture;
        gl.bindVertexArray(CommonRenderingResources.PlaneShapeVAO);

        gl.useProgram(curShader.ShaderProgram);
        const uniformsLocation = curShader.UniformParametersLocationList;

        //Constants
        gl.uniform1f(uniformsLocation.ViewportRatio, vecField.DensityFieldXYRatio);
        gl.uniform2f(uniformsLocation.PositionNDC, posNDC.x, posNDC.y);
        gl.uniform2f(uniformsLocation.Scale, scale.x, scale.y);
        if (colorFilter) {
            gl.uniform3f(uniformsLocation.ColorFilter, colorFilter.x, colorFilter.y, colorFilter.z);
        } else {
            gl.uniform3f(uniformsLocation.ColorFilter, 1.0, 1.0, 1.0);
        }

        gl.activeTexture(gl.TEXTURE0 + 1);
        gl.bindTexture(gl.TEXTURE_2D, pTexture);
        gl.uniform1i(uniformsLocation.ColorTexture, 1);

        gl.drawArrays(gl.TRIANGLES, 0, 6);

        gl.disable(gl.BLEND);
    }

    InjectDensitySceneColor(
        gl: WebGL2RenderingContext,
        vecField: VectorField,
        posNDC: Vector2,
        scale: Vector2,
        colorFilter: Vector3 | null = null,
    ) {
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        gl.blendEquation(gl.MAX);

        vecField.BindCurDensityAlphaRT(gl);

        const curShader = this.ShaderInjectDenistySceneColor;
        gl.bindVertexArray(CommonRenderingResources.PlaneShapeVAO);

        gl.useProgram(curShader.ShaderProgram);
        const uniformsLocation = curShader.UniformParametersLocationList;

        //Constants
        gl.uniform1f(uniformsLocation.ViewportRatio, vecField.DensityFieldXYRatio);
        gl.uniform2f(uniformsLocation.ViewportSize, vecField.DensityNumCells.x, vecField.DensityNumCells.y);
        gl.uniform2f(uniformsLocation.PositionNDC, posNDC.x, posNDC.y);
        gl.uniform2f(uniformsLocation.Scale, scale.x, scale.y);
        if (colorFilter) {
            gl.uniform3f(uniformsLocation.ColorFilter, colorFilter.x, colorFilter.y, colorFilter.z);
        } else {
            gl.uniform3f(uniformsLocation.ColorFilter, 1.0, 1.0, 1.0);
        }

        gl.activeTexture(gl.TEXTURE0 + 1);
        gl.bindTexture(gl.TEXTURE_2D, GRenderTargets.SceneColorTexture!);
        gl.uniform1i(uniformsLocation.ColorTexture, 1);

        gl.drawArrays(gl.TRIANGLES, 0, 6);

        gl.disable(gl.BLEND);
    }
}
